import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Play, RefreshCw, Loader2, Save, RotateCcw, Eye, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const API = `${window.location.origin}/api/becoming-her/admin`;

const SETTING_FIELDS = [
  ['dea_url', 'DEA_URL (Email 4 button)'],
  ['journal_amazon_url', 'JOURNAL_AMAZON_URL (Email 3 after Nov 10)'],
  ['pdf_url', 'PDF link (Email 1 button)'],
  ['instagram_url', 'Instagram URL'],
  ['mailing_address', 'Mailing address (footer)'],
  ['from_name', 'From name'],
  ['from_email', 'From email'],
  ['reply_to', 'Reply-To'],
  ['test_email', 'Test emails go to'],
  ['early_access_date', 'Email 4 Version A date (YYYY-MM-DD)'],
  ['daily_limit', 'Resend daily limit'],
  ['reserve_for_email1', 'Daily slots reserved for Email 1'],
  ['exclude_emails', 'Test addresses excluded from backfill (comma separated)'],
];

const Stat = ({ label, value, testId }) => (
  <div className="bg-[#EAD8C2]/20 rounded-xl p-4" data-testid={testId}>
    <p className="text-xs text-[#AA9283] uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-serif text-[#726158] mt-1">{value}</p>
  </div>
);

const EmailEditor = ({ item, onSave, onReset, onPreview }) => {
  const [form, setForm] = useState({ subject: item.subject, preview: item.preview, body: item.body });
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm({ subject: item.subject, preview: item.preview, body: item.body }), [item]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { setSaving(true); await onSave(item.key, form); setSaving(false); };
  return (
    <div className="bg-white/70 rounded-xl border border-[#EAD8C2] p-4 space-y-3" data-testid={`bh-editor-${item.key}`}>
      <h3 className="font-medium text-[#726158]">{item.label}</h3>
      <Input value={form.subject} onChange={set('subject')} placeholder="Subject" className="input-light" data-testid={`bh-subject-${item.key}`} />
      <Input value={form.preview} onChange={set('preview')} placeholder="Preview text" className="input-light" data-testid={`bh-preview-${item.key}`} />
      <Textarea value={form.body} onChange={set('body')} rows={14} className="input-light font-mono text-xs" data-testid={`bh-body-${item.key}`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-[#726158] hover:bg-[#5e4f47] text-white" data-testid={`bh-save-${item.key}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
        </Button>
        <Button size="sm" variant="outline" onClick={() => onPreview(item.key)} data-testid={`bh-preview-btn-${item.key}`}><Eye className="h-4 w-4 mr-1" /> Preview</Button>
        <Button size="sm" variant="ghost" onClick={() => onReset(item.key)} className="text-[#AA9283]" data-testid={`bh-reset-${item.key}`}><RotateCcw className="h-4 w-4 mr-1" /> Reset to original</Button>
      </div>
    </div>
  );
};

const BackfillPanel = ({ headers, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const loadPreview = async () => {
    setBusy(true); setResults(null);
    try {
      const r = await axios.get(`${API}/backfill/preview`, headers());
      setPreview(r.data); setSelected(r.data.missing.map((m) => m.email));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read Kit'); }
    setBusy(false);
  };
  const toggle = (email) => setSelected((s) => (s.includes(email) ? s.filter((x) => x !== email) : [...s, email]));
  const run = async () => {
    if (!window.confirm(`Add ${selected.length} people to the sequence and send Email 1 to each of them now?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/backfill/run`, { emails: selected }, headers());
      setResults(r.data.results);
      toast.success(`Email 1 sent to ${r.data.results.filter((x) => x.email1_sent).length} of ${r.data.results.length}`);
      await onDone(); await loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
    setBusy(false);
  };

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="bh-backfill-panel">
      <h3 className="font-medium text-[#726158]">Backfill from Kit</h3>
      <p className="text-xs text-[#AA9283] mt-1 mb-3">Finds everyone tagged <code>lead-becoming-her</code> in Kit who is missing from this database. Nothing is sent until you review the list and confirm. Test addresses are excluded (edit in settings).</p>
      <Button variant="outline" onClick={loadPreview} disabled={busy} data-testid="bh-backfill-preview">
        {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Check Kit for missing people
      </Button>
      {preview && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-[#AA9283]">{preview.kit_total} tagged in Kit · {preview.missing.length} missing here · {preview.skipped.length} skipped</p>
          {preview.missing.length === 0 ? <p className="text-sm text-[#726158]">Everyone tagged in Kit is already in the sequence.</p> : (
            <div className="space-y-2" data-testid="bh-backfill-list">
              {preview.missing.map((m) => (
                <label key={m.email} className="flex items-center gap-3 text-sm text-[#726158] bg-white/60 rounded-lg px-3 py-2 border border-[#EAD8C2]">
                  <input type="checkbox" checked={selected.includes(m.email)} onChange={() => toggle(m.email)} data-testid={`bh-backfill-check-${m.email}`} />
                  <span className="font-medium">{m.first_name || '—'}</span><span>{m.email}</span>
                  <span className="text-xs text-[#AA9283] ml-auto">tagged {m.tagged_in_kit ? new Date(m.tagged_in_kit).toLocaleDateString() : '?'}</span>
                </label>
              ))}
              <Button onClick={run} disabled={busy || selected.length === 0} className="bg-[#726158] hover:bg-[#5e4f47] text-white" data-testid="bh-backfill-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Add {selected.length} & send Email 1 now
              </Button>
            </div>
          )}
          {preview.skipped.length > 0 && <p className="text-xs text-[#AA9283]">Skipped: {preview.skipped.map((s) => `${s.email} (${s.reason})`).join(' · ')}</p>}
          {results && (
            <ul className="text-xs text-[#726158] space-y-1" data-testid="bh-backfill-results">
              {results.map((r) => <li key={r.email}>{r.email1_sent ? '✓' : '✗'} {r.email}{r.note ? ` — ${r.note}` : ''}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const BecomingHerEmails = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [signups, setSignups] = useState([]);
  const [busy, setBusy] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const headers = useCallback(() => ({ headers: getAuthHeader() }), [getAuthHeader]);

  const load = useCallback(async () => {
    const [o, s] = await Promise.all([axios.get(`${API}/overview`, headers()), axios.get(`${API}/signups?limit=50`, headers())]);
    setData(o.data); setSettings(o.data.settings); setSignups(s.data.signups);
  }, [headers]);

  useEffect(() => { load().catch(() => toast.error('Could not load Becoming Her data')); }, [load]);

  const saveContent = async (key, form) => {
    try { await axios.put(`${API}/content/${key}`, form, headers()); toast.success('Email saved'); await load(); }
    catch { toast.error('Save failed'); }
  };
  const resetContent = async (key) => {
    try { await axios.post(`${API}/content/${key}/reset`, {}, headers()); toast.success('Reset to original text'); await load(); }
    catch { toast.error('Reset failed'); }
  };
  const openPreview = async (key) => {
    try { const r = await axios.get(`${API}/preview/${key}`, { ...headers(), responseType: 'text' }); setPreviewHtml(r.data); }
    catch { toast.error('Preview failed'); }
  };
  const saveSettings = async () => {
    setBusy('settings');
    try {
      const payload = { ...settings, daily_limit: Number(settings.daily_limit), reserve_for_email1: Number(settings.reserve_for_email1) };
      await axios.put(`${API}/settings`, payload, headers()); toast.success('Settings saved'); await load();
    } catch { toast.error('Settings save failed'); }
    setBusy('');
  };
  const sendTest = async () => {
    setBusy('test');
    try {
      const r = await axios.post(`${API}/send-test`, { to: settings.test_email }, headers());
      const ok = r.data.results.filter((x) => x.ok).length;
      toast.success(`Sent ${ok}/5 test emails to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
    setBusy('');
  };
  const runDaily = async () => {
    setBusy('run');
    try {
      const r = await axios.post(`${API}/run-daily`, {}, headers());
      const s = r.data;
      toast.success(s.saturday ? 'Skipped: Saturday (no sends)' : `Run done — E1 ${s.e1}, E2 ${s.e2}, E3 ${s.e3}, E4 ${s.e4}, held for cap ${s.skipped_cap}`);
      await load();
    } catch { toast.error('Run failed'); }
    setBusy('');
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#87A6A7]" /></div>;
  const { stats } = data;

  return (
    <div className="space-y-6" data-testid="bh-admin-panel">
      <div className="glass-card rounded-2xl p-6 border-2 border-[#87A6A7]/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#87A6A7]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#87A6A7]" /></div>
          <div>
            <h2 className="font-serif text-xl text-[#726158]">Becoming Her Welcome Sequence</h2>
            <p className="text-[#AA9283] text-sm">4 emails via Resend · daily job 9:00 am ET, Sun–Fri · {data.cron_configured ? 'scheduler key configured' : 'scheduler key MISSING'}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Signups" value={stats.total} testId="bh-stat-total" />
          <Stat label="Unsubscribed" value={stats.unsubscribed} testId="bh-stat-unsub" />
          <Stat label="Email 1 / 2 / 3 / 4 sent" value={`${stats.sent.e1} / ${stats.sent.e2} / ${stats.sent.e3} / ${stats.sent.e4}`} testId="bh-stat-sent" />
          <Stat label="Resend sends today" value={`${stats.sent_today_all} / ${settings.daily_limit}`} testId="bh-stat-today" />
        </div>
        {stats.pending_e1 > 0 && <p className="text-xs text-[#B23A48] mt-3">{stats.pending_e1} Email 1 waiting (daily cap or failed) — retried on next run.</p>}
        {stats.errors > 0 && <p className="text-xs text-[#B23A48] mt-1">{stats.errors} subscriber(s) with a send error — see table.</p>}
        {stats.kit_unsynced > 0 && <p className="text-xs text-[#B23A48] mt-1">{stats.kit_unsynced} subscriber(s) not yet tagged in Kit.</p>}
        {data.last_run && <p className="text-xs text-[#AA9283] mt-3">Last run {new Date(data.last_run.ran_at).toLocaleString()} · E1 {data.last_run.e1}, E2 {data.last_run.e2}, E3 {data.last_run.e3}, E4 {data.last_run.e4}{data.last_run.saturday ? ' · skipped (Saturday)' : ''}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={sendTest} disabled={!!busy} className="bg-[#726158] hover:bg-[#5e4f47] text-white" data-testid="bh-send-test">
            {busy === 'test' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send all 5 test emails to {settings.test_email}
          </Button>
          <Button variant="outline" onClick={runDaily} disabled={!!busy} data-testid="bh-run-daily">
            {busy === 'run' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Run daily job now
          </Button>
          <Button variant="ghost" onClick={load} className="text-[#AA9283]" data-testid="bh-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>
      </div>

      <BackfillPanel headers={headers} onDone={load} />

      <div className="glass-card rounded-2xl p-6">
        <h3 className="font-medium text-[#726158] mb-3">Links & settings</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {SETTING_FIELDS.map(([k, label]) => (
            <label key={k} className="text-xs text-[#AA9283]">{label}
              <Input value={settings[k] ?? ''} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} className="input-light mt-1" data-testid={`bh-setting-${k}`} />
            </label>
          ))}
        </div>
        <Button onClick={saveSettings} disabled={busy === 'settings'} className="mt-4 bg-[#726158] hover:bg-[#5e4f47] text-white" data-testid="bh-save-settings">
          {busy === 'settings' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save settings
        </Button>
      </div>

      <div className="glass-card rounded-2xl p-6 space-y-4">
        <div>
          <h3 className="font-medium text-[#726158]">Email text</h3>
          <p className="text-xs text-[#AA9283] mt-1">
            Tokens: {'{first_name}'} {'{PDF_URL}'} {'{DEA_URL}'} {'{JOURNAL_AMAZON_URL}'} {'{INSTAGRAM_URL}'} · Button: [BUTTON: Label -&gt; URL] · Gray box: [GRAYBOX]…[/GRAYBOX] · Date rules: [IF before:2026-11-10]…[/IF] / [IF onorafter:2026-11-10]…[/IF] · Lines starting with "- " become bullets.
          </p>
        </div>
        {data.content.map((item) => <EmailEditor key={item.key} item={item} onSave={saveContent} onReset={resetContent} onPreview={openPreview} />)}
      </div>

      <div className="glass-card rounded-2xl p-6">
        <h3 className="font-medium text-[#726158] mb-3 flex items-center gap-2"><Users className="h-4 w-4" /> Recent signups</h3>
        {data.by_source.length > 0 && (
          <p className="text-xs text-[#AA9283] mb-3">By source: {data.by_source.map((b) => `${b.source} (${b.count})`).join(' · ')}</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bh-signups-table">
            <thead><tr className="text-left text-[#AA9283] border-b border-[#EAD8C2]"><th className="py-2 pr-3">Email</th><th className="pr-3">Name</th><th className="pr-3">Signed up (ET)</th><th className="pr-3">Source</th><th className="pr-3">E1</th><th className="pr-3">E2 due</th><th className="pr-3">E3 due</th><th className="pr-3">E4 due</th><th>Status</th></tr></thead>
            <tbody>
              {signups.map((s) => (
                <tr key={s.email} className="border-b border-[#EAD8C2]/60 text-[#726158]">
                  <td className="py-2 pr-3">{s.email}</td><td className="pr-3">{s.first_name}</td>
                  <td className="pr-3">{new Date(s.signup_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}</td>
                  <td className="pr-3">{s.utm_source || '—'}</td>
                  <td className="pr-3">{s.sent?.e1 ? '✓' : (s.last_error ? '✗ error' : '…')}</td>
                  <td className="pr-3">{s.sent?.e2 ? '✓' : s.due.e2}</td><td className="pr-3">{s.sent?.e3 ? '✓' : s.due.e3}</td>
                  <td className="pr-3">{s.sent?.e4 ? `✓ ${s.e4_version}` : s.due.e4}</td>
                  <td>{s.unsubscribed ? 'Unsubscribed' : 'Active'}</td>
                </tr>
              ))}
              {signups.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-[#AA9283]">No signups yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {previewHtml && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)} data-testid="bh-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <iframe title="Email preview" srcDoc={previewHtml} className="w-full h-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
};
